import { useQuery } from "react-query";
import { useEffect, useState } from "react";

import MainCard from "../../components/layout/MainCard/MainCard";
import LoadingWrapper from "../../components/Loading/LoadingWrapper";
import { currencyFormat } from "../../helpers/functions";
import { externalAttributeText } from "../../helpers/orderExternalAttributes";
import useAPI from "../../hooks/useAPI";
import { GlobalQueries } from "../../hooks/useRefreshQuery";
import DataTable from "components/DataTable/DataTable";
import EmptyState from "components/Empty/EmptyState";
import SearchBar from "components/UI/SearchBar";
import PaginationUI from "../../components/UI/PaginationUI";
import SidebarHeader from "../../components/layout/WithFixedSidebar/SidebarHeader/SidebarHeader";
import icons from "helpers/icons";
import scss from "./OrdersPage.module.scss";
import tblFields from "./tblFields.json";
import { useTranslation } from "react-i18next";

/**
 * Show the list of previous orders.
 */
const OrdersPage = () => {
    const { t } = useTranslation();
    const [search, setSearch] = useState("");

    const api = useAPI();
    const apiPath = "/previous-orders";
    const limit = 12;
    const [page, setPage] = useState(1);
    // Load all products in the cart
    const { data: orders, isLoading } = useQuery([GlobalQueries.Orders, search, page, limit], () => api.list(apiPath, { search, page, limit }));

    // Format the items
    const convertDate = (date) => {
        const options = {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "numeric",
            minute: "numeric",
            hour12: false,
        };

        const dateObj = new Date(date);
        const formattedDate = dateObj
            .toLocaleString("sr-RS", options)
            .replace(/,/g, "")
            .replace(/(\d+)\D+(\d+)\D+(\d+)\D+(\d+):(\d+)/, "$1.$2.$3. $4:$5");
        return formattedDate;
    };
    // External attributes configured in the CMS for the order list - one extra column per attribute
    const externalColumns = [];
    (orders?.items ?? []).forEach((item) =>
        (item.external_attributes ?? []).forEach((attribute) => {
            if (!externalColumns.some((column) => column.field === `external_${attribute.slug}`)) {
                externalColumns.push({ field: `external_${attribute.slug}`, label: attribute.name, showOnMobile: false });
            }
        })
    );
    const columns = [...tblFields.slice(0, 2), ...externalColumns, ...tblFields.slice(2)];

    const items = (orders?.items ?? []).map((item) => ({
        ...item,
        ...Object.fromEntries((item.external_attributes ?? []).map((attribute) => [`external_${attribute.slug}`, externalAttributeText(attribute)])),
        total: currencyFormat(item.total, item.currency),
        status: <span className={scss.status}>{item.status}</span>,
        created_at: convertDate(item.created_at),
    }));
    const [currentPage, setCurrentPage] = useState(1);
    useEffect(() => setCurrentPage(orders?.pagination.selected_page ?? 1), [orders, page, orders?.pagination.selected_page]);

    return (
        <MainCard>
            <SidebarHeader title={t("orders-default-title")} icon={icons.cart} mobileOnly />
            <div className="page-container">
                <div className={`p page-content`}>
                    <div className={scss.toolbar}>
                        <div>
                            <h4 className={scss.desktopTitle}>{t("orders-default-title")}</h4>
                            <p className={scss.count}>Ukupno porudžbina: <strong>{orders?.pagination?.total_items ?? 0}</strong></p>
                        </div>
                        <SearchBar placeholder={"orders-default-search_placeholder"} onSubmit={(e) => e.preventDefault()} search={search} setSearch={setSearch} page={page} setPage={setPage} mobileVisible />
                    </div>

                    <LoadingWrapper isLoading={isLoading}>
                        {items.length > 0 ? <div className={scss.list}><DataTable columns={columns} data={items} apiUrl={apiPath} mobileCards previewLabel="Otvori detalje" /></div> : (
                            <EmptyState
                                kind="orders"
                                title={search ? "Nema rezultata" : "Nema kupovina"}
                                description={search ? "Nema porudžbina za unetu pretragu." : "Trenutno nemate evidentiranih porudžbina."}
                            >
                                {search && <button type="button" onClick={() => { setSearch(""); setPage(1); }}>Obriši pretragu</button>}
                            </EmptyState>
                        )}
                    </LoadingWrapper>

                    {items.length > 0 && <PaginationUI
                        itemLabel="porudžbina"
                        singleItemLabel="porudžbina"
                        activePage={currentPage}
                        setPage={setPage}
                        numberOfDisplayed={orders?.pagination?.items_per_page ?? 12}
                        numberOfProducts={orders?.pagination.total_items ?? 0}
                        changePage={() => {}}
                        page1={page}
                    />}
                </div>
            </div>
        </MainCard>
    );
};

export default OrdersPage;
